import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Database, RefreshCw, CheckCircle2, AlertTriangle, Cloud, Play,
  Activity, Clock, Building2, Shield,
} from 'lucide-react';

/**
 * BackupPage — Admin dashboard for the Atlas MongoDB backup worker
 * (iter 130). Shows connection health, last sync summary, per-collection
 * progress, and a "Run backup now" trigger button.
 */
export default function BackupPage() {
  const [state, setState] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);
  const [running, setRunning] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        api.get('/api/admin/backup/status'),
        api.get('/api/admin/backup/tenants').catch(() => ({ data: null })),
      ]);
      setState(s.data);
      setTenants(t.data);
    } catch (e) {
      toast.error('Failed to load backup status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    // Poll every 8s so the UI reflects the background worker's progress live.
    const t = setInterval(fetchStatus, 8000);
    return () => clearInterval(t);
  }, [fetchStatus]);

  const testConnection = async () => {
    setPinging(true);
    try {
      const r = await api.post('/api/admin/backup/test-connection');
      if (r.data?.ok) {
        toast.success(`Atlas reachable → ${r.data.db}`);
      } else {
        toast.error(r.data?.error || 'Ping failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ping failed');
    } finally {
      setPinging(false);
      fetchStatus();
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post('/api/admin/backup/run-now');
      if (r.data?.ok) {
        toast.success('Backup started — watch the per-collection progress below');
      } else {
        toast.error(r.data?.error || 'Could not start backup');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backup trigger failed');
    } finally {
      setRunning(false);
      setTimeout(fetchStatus, 500);
    }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const r = await api.get('/api/admin/tenant-isolation/audit');
      setAudit(r.data);
      const leaks = r.data?.summary?.leak_count || 0;
      const orphans = r.data?.summary?.orphan_count || 0;
      if (leaks === 0 && orphans === 0) {
        toast.success('All tenant data is properly isolated');
      } else {
        toast.warning(`${leaks} leaks · ${orphans} orphan groups · review below`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
    } finally {
      setAuditing(false);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm('Backfill tenant_id on legacy wallet/game-account/notification docs? Safe + idempotent.')) return;
    setBackfilling(true);
    try {
      const r = await api.post('/api/admin/tenant-isolation/backfill');
      const updated = r.data?.total_updated || 0;
      toast.success(`Backfill complete · ${updated} docs enriched with tenant_id`);
      // Re-run audit to show updated state
      runAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading || !state) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-400" data-testid="backup-loading">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading backup status…
      </div>
    );
  }

  const isHealthy = state.last_status === 'ok' || state.last_status === 'pinged';
  const isRunningLive = state.running === true;
  const statusColor = isHealthy
    ? 'text-green-400'
    : state.last_status === 'error'
      ? 'text-red-400'
      : 'text-amber-400';

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="admin-backup-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cloud className="w-6 h-6 text-sky-400" />
            MongoDB Atlas Backup
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Mirrors every collection to a remote Atlas cluster so that a lost VPS doesn't mean lost data.
            Point <code className="px-1 rounded bg-white/10 text-[11px]">MONGO_URL</code> at the backup URI on a fresh VPS and you're live again.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={testConnection}
            disabled={pinging}
            variant="outline"
            className="border-white/10"
            data-testid="backup-test-btn"
          >
            <Activity className={`w-4 h-4 mr-2 ${pinging ? 'animate-pulse' : ''}`} />
            {pinging ? 'Pinging…' : 'Test Connection'}
          </Button>
          <Button
            onClick={runNow}
            disabled={running || isRunningLive || !state.configured}
            className="bg-[#D4AF37] hover:bg-[#F5D76E] text-black font-bold"
            data-testid="backup-run-btn"
          >
            <Play className="w-4 h-4 mr-2" />
            {isRunningLive ? 'Running…' : 'Run Backup Now'}
          </Button>
        </div>
      </div>

      {/* STATUS CARDS */}
      <div className="grid md:grid-cols-4 gap-3">
        <KpiCard
          label="Connection"
          value={state.configured ? (isHealthy ? 'Healthy' : 'Degraded') : 'Not configured'}
          color={state.configured ? (isHealthy ? '#22c55e' : '#f59e0b') : '#6b7280'}
          icon={state.configured ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          testid="backup-kpi-connection"
          sub={state.target_uri_host || 'no host'}
        />
        <KpiCard
          label="Last Run"
          value={state.last_run_at ? relTime(state.last_run_at) : 'Never'}
          color={isHealthy ? '#22c55e' : '#f59e0b'}
          icon={<Clock className="w-4 h-4" />}
          testid="backup-kpi-last-run"
          sub={state.last_run_at ? new Date(state.last_run_at).toLocaleString() : '—'}
        />
        <KpiCard
          label="Last Sync Docs"
          value={(state.last_docs_copied || 0).toLocaleString()}
          color="#6366f1"
          icon={<Database className="w-4 h-4" />}
          testid="backup-kpi-docs"
          sub={`${state.last_duration_sec || 0}s duration`}
        />
        <KpiCard
          label="Interval"
          value={`${state.interval_hours || 6}h`}
          color="#D4AF37"
          icon={<RefreshCw className="w-4 h-4" />}
          testid="backup-kpi-interval"
          sub={`prefix: ${state.target_db_prefix || '—'}`}
        />
      </div>

      {/* Current status line */}
      <div
        className="rounded-2xl p-4 flex items-center gap-3"
        style={{
          background: 'rgba(255,255,255,0.02)',
          border: `1px solid ${isHealthy ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}`,
        }}
        data-testid="backup-status-line"
      >
        {isRunningLive ? (
          <RefreshCw className="w-5 h-5 animate-spin text-sky-400" />
        ) : isHealthy ? (
          <CheckCircle2 className="w-5 h-5 text-green-400" />
        ) : (
          <AlertTriangle className="w-5 h-5 text-amber-400" />
        )}
        <div className="flex-1">
          <p className={`text-sm font-semibold ${statusColor}`}>
            {isRunningLive
              ? 'Backup in progress — syncing collections to Atlas…'
              : `Status: ${state.last_status || 'unknown'}`}
          </p>
          {state.last_error && (
            <p className="text-xs text-red-400 mt-0.5">Last error: {state.last_error}</p>
          )}
          {!state.configured && (
            <p className="text-xs text-gray-400 mt-0.5">
              Set <code>MONGO_BACKUP_URL</code> in <code>backend/.env</code> to enable backups.
            </p>
          )}
        </div>
      </div>

      {/* Per-Tenant Atlas DBs */}
      {tenants?.tenants && Object.keys(tenants.tenants).length > 0 && (
        <div
          className="rounded-2xl p-5 space-y-4 border border-white/5"
          style={{ background: 'rgba(255,255,255,0.02)' }}
          data-testid="per-tenant-atlas-section"
        >
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-emerald-400 mt-0.5" />
            <div className="flex-1">
              <h2 className="font-bold text-white text-base">Per-Tenant Atlas Databases</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Each tenant's data is mirrored into its own Atlas database — `<code className="text-emerald-400">{tenants.target_db_prefix || 'teamlobby'}_&lt;slug&gt;</code>` — so backup is fully isolated per tenant.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {Object.entries(tenants.tenants).map(([slug, info]) => (
              <div
                key={slug}
                className="rounded-xl p-3 border bg-black/30"
                style={{ borderColor: slug === 'main' ? 'rgba(99,102,241,0.3)' : 'rgba(34,197,94,0.3)' }}
                data-testid={`tenant-atlas-${slug}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm" style={{ color: slug === 'main' ? '#818cf8' : '#22c55e' }}>
                    {slug}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-300">
                    {(info.docs_copied || 0).toLocaleString()} docs
                  </span>
                </div>
                <p className="font-mono text-[11px] text-gray-400 mt-1 truncate">{info.atlas_db}</p>
                <p className="text-[10px] text-gray-500 mt-1">
                  {Object.keys(info.collections || {}).length} collections
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tenant Isolation Audit */}
      <div
        className="rounded-2xl p-5 space-y-4 border border-white/5"
        style={{ background: 'rgba(255,255,255,0.02)' }}
        data-testid="tenant-isolation-section"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <h2 className="font-bold text-white text-base">Tenant Isolation Audit</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Scans every collection for missing or orphaned `tenant_id` to verify each tenant's data is fully separated.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runBackfill}
              disabled={backfilling}
              variant="outline"
              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="backfill-tenant-id-btn"
            >
              {backfilling ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Backfill tenant_id
            </Button>
            <Button
              onClick={runAudit}
              disabled={auditing}
              className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
              data-testid="run-isolation-audit-btn"
            >
              {auditing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
              Run Audit
            </Button>
          </div>
        </div>

        {audit?.summary && (
          <div className="grid md:grid-cols-4 gap-3">
            <KpiCard
              label="Collections"
              value={audit.summary.total_collections}
              color="#6366f1"
              icon={<Database className="w-4 h-4" />}
              testid="audit-kpi-collections"
              sub={`${(audit.summary.total_docs || 0).toLocaleString()} docs`}
            />
            <KpiCard
              label="Tenants"
              value={Object.keys(audit.summary.tenants || {}).length}
              color="#22c55e"
              icon={<Building2 className="w-4 h-4" />}
              testid="audit-kpi-tenants"
              sub="active tenants"
            />
            <KpiCard
              label="Leaks"
              value={audit.summary.leak_count}
              color={audit.summary.leak_count > 0 ? '#ef4444' : '#22c55e'}
              icon={audit.summary.leak_count > 0 ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              testid="audit-kpi-leaks"
              sub="missing tenant_id in scoped collections"
            />
            <KpiCard
              label="Orphans"
              value={audit.summary.orphan_count}
              color={audit.summary.orphan_count > 0 ? '#f59e0b' : '#22c55e'}
              icon={audit.summary.orphan_count > 0 ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
              testid="audit-kpi-orphans"
              sub="docs pointing at deleted tenants"
            />
          </div>
        )}

        {audit?.collections && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="text-left py-2 px-2">Collection</th>
                  <th className="text-right py-2 px-2">Total</th>
                  <th className="text-right py-2 px-2">Missing</th>
                  <th className="text-right py-2 px-2">Orphaned</th>
                  <th className="text-left py-2 px-2">Tenants</th>
                  <th className="text-left py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {audit.collections
                  .filter(c => (c.total || 0) > 0)
                  .sort((a, b) => (b.total || 0) - (a.total || 0))
                  .map(c => (
                    <tr key={c.name} className="border-b border-white/5 hover:bg-white/5" data-testid={`audit-row-${c.name}`}>
                      <td className="py-1.5 px-2 font-mono text-[11px] text-white">{c.name}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-gray-300">{(c.total || 0).toLocaleString()}</td>
                      <td className={`py-1.5 px-2 text-right tabular-nums ${c.leak ? 'text-red-400 font-bold' : 'text-gray-500'}`}>
                        {c.missing_tenant_id || 0}
                      </td>
                      <td className={`py-1.5 px-2 text-right tabular-nums ${(c.orphaned || 0) > 0 ? 'text-amber-400 font-bold' : 'text-gray-500'}`}>
                        {c.orphaned || 0}
                      </td>
                      <td className="py-1.5 px-2 text-[10px] text-emerald-300 font-mono">
                        {Object.keys(c.by_tenant || {}).length}
                      </td>
                      <td className="py-1.5 px-2 text-[10px]">
                        {c.leak ? (
                          <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> leak</span>
                        ) : c.platform_wide ? (
                          <span className="text-indigo-300">platform-wide</span>
                        ) : (
                          <span className="text-green-400">isolated</span>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {!audit && (
          <p className="text-xs text-gray-500 italic">Click <strong className="text-amber-300">Run Audit</strong> to scan tenant data isolation.</p>
        )}
      </div>

      {/* Per-collection table */}
      {state.collections && Object.keys(state.collections).length > 0 && (
        <div
          className="rounded-2xl p-5 space-y-3 border border-white/5"
          style={{ background: 'rgba(255,255,255,0.02)' }}
          data-testid="backup-collections-section"
        >
          <div className="flex items-start gap-3">
            <Database className="w-5 h-5 text-indigo-400 mt-0.5" />
            <div>
              <h2 className="font-bold text-white text-base">Per-Collection Snapshot</h2>
              <p className="text-xs text-gray-400 mt-0.5">
                From the most recent sync — source vs target counts must match for a healthy collection.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="text-left py-2 px-2">Collection</th>
                  <th className="text-right py-2 px-2">Source</th>
                  <th className="text-right py-2 px-2">Target</th>
                  <th className="text-right py-2 px-2">Copied</th>
                  <th className="text-right py-2 px-2">Duration</th>
                  <th className="text-left py-2 px-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(state.collections)
                  .sort(([, a], [, b]) => (b.source_count || 0) - (a.source_count || 0))
                  .map(([name, row]) => {
                    const err = row.error;
                    const mismatch = !err && row.source_count !== undefined
                      && row.target_count !== undefined
                      && row.source_count !== row.target_count;
                    return (
                      <tr
                        key={name}
                        className="border-b border-white/5 hover:bg-white/5"
                        data-testid={`backup-row-${name}`}
                      >
                        <td className="py-2 px-2 font-mono text-xs text-white">{name}</td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-300">
                          {row.source_count != null ? row.source_count.toLocaleString() : '—'}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-300">
                          {row.target_count != null ? row.target_count.toLocaleString() : '—'}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-indigo-300">
                          {(row.copied || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums text-gray-400">
                          {row.duration_sec != null ? `${row.duration_sec}s` : '—'}
                        </td>
                        <td className="py-2 px-2">
                          {err ? (
                            <span className="text-[11px] text-red-400 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> {err.slice(0, 50)}
                            </span>
                          ) : mismatch ? (
                            <span className="text-[11px] text-amber-400">drift</span>
                          ) : (
                            <span className="text-[11px] text-green-400">synced</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DR playbook */}
      <div
        className="rounded-xl p-4 text-sm text-gray-300 leading-relaxed"
        style={{
          background: 'linear-gradient(135deg, rgba(56,189,248,0.08), rgba(99,102,241,0.05))',
          border: '1px solid rgba(56,189,248,0.25)',
        }}
        data-testid="backup-playbook"
      >
        <p className="font-bold text-white mb-2">🛟 Disaster Recovery Playbook</p>
        <ol className="list-decimal ml-5 space-y-1 text-xs">
          <li>Clone the TeamLobby repo on a new VPS.</li>
          <li>In <code className="text-[11px] px-1 rounded bg-white/10">backend/.env</code>, set <code>MONGO_URL</code> to the Atlas URI currently in <code>MONGO_BACKUP_URL</code>.</li>
          <li>Choose which tenant to restore (or `<code>teamlobby_main</code>` for platform). Set <code>DB_NAME</code> in <code>backend/.env</code> to that Atlas DB.</li>
          <li>Run <code>sudo supervisorctl restart all</code>. Every user, wallet, transaction is already there — players log in with their existing creds and resume.</li>
          <li>Once stable, flip the new VPS's local Mongo + Atlas roles so the new node becomes the primary writer and Atlas resumes as pure backup.</li>
        </ol>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, icon, sub, testid }) {
  return (
    <div
      className="rounded-xl p-3 border"
      style={{ background: '#131822', borderColor: `${color}33` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        <span style={{ color }}>{icon}</span>
        {label}
      </div>
      <div className="text-lg font-bold mt-1 truncate" style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-gray-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function relTime(iso) {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  } catch { return '—'; }
}
